import { DASHBOARD_TILES } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import "./dashboard-tile";
import "../employee-absence-list";
import "../avatar";
import { app, router } from "../../init";
import "../popover";

import { Task } from "@lit/task";
import { shared } from "../../styles";
import { FullDateFormatter, RelativeDayFormatter } from "../../utils/temporal";

@customElement("ptc-dashboard-tile-expiring-contracts")
export class DashboardTileExpiringContracts extends LitElement {
    @property({ type: Object, attribute: false })
    timeOffset: Temporal.DurationLike = { days: 30 };

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }

            [part="employee-list"] {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                padding: 1rem;
                margin: 0;
            }

            [part="employee"] {
                display: grid;
                gap: 0 0.5rem;
                grid-template-columns: min-content 1fr;
                place-items: center start;
            }

            [part="employee-name"] {
                font-size: 1.15em;
                font-weight: 600;
                transition: color 0.2s ease-in-out;
            }

            [part="employee"]:hover {
                cursor: pointer;
            }
            [part="employee"]:hover [part="employee-name"] {
                color: var(--blue);
            }

            [part="details"] {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                justify-content: center;
                min-height: 1.5rem;
            }

            [part="time-left"] {
                width: fit-content;
                grid-column: 2;
            }
        `,
    ];

    private _expiringContractsTask = new Task(this, {
        task: async () => {
            const mappedEmployeesWithExpiringContracts = app.employees
                .filter((employee) => {
                    if (employee.contracts.length === 0) return false;

                    const openContract = employee.contracts.find((contract) => !contract.end);
                    if (openContract) return false;

                    const mostRecentContract = employee.contracts
                        .filter((contract) => contract.end)
                        .sort((a, b) => Temporal.PlainDate.compare(b.end!, a.end!))[0];

                    const now = Temporal.Now.plainDateISO();
                    const cutoffDate = now.add(this.timeOffset);

                    return (
                        Temporal.PlainDate.compare(mostRecentContract.end!, now) >= 0 &&
                        Temporal.PlainDate.compare(mostRecentContract.end!, cutoffDate) < 0
                    );
                })
                .map((employee) => ({
                    employee,
                    contract: [...employee.contracts]
                        .sort((a, b) => {
                            if (!a.end || !b.end) return 0;
                            return Temporal.PlainDate.compare(b.end, a.end);
                        })
                        .find(
                            (contract) =>
                                contract.end &&
                                Temporal.PlainDate.compare(
                                    contract.end,
                                    Temporal.Now.plainDateISO().add(this.timeOffset)
                                ) < 0
                        ),
                }))
                .sort((a, b) => {
                    if (!a.contract?.end || !b.contract?.end) return 0;
                    return Temporal.PlainDate.compare(a.contract.end, b.contract.end);
                });

            return mappedEmployeesWithExpiringContracts;
        },

        args: () => [],
        autoRun: true,
    });

    render() {
        return html`
            <ptc-dashboard-tile
                label=${DASHBOARD_TILES.expiringContracts}
                icon="file-contract"
                .size=${{ inline: 1, block: 2 }}
            >
                ${this._expiringContractsTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-spinner
                            class="place-self-center double-padded"
                            active
                            style="font-size: 1rem"
                        ></ptc-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">Verträge konnten nicht geladen werden!</div>`,
                    complete: (employeeWithExpiringContract) => {
                        if (!employeeWithExpiringContract.length) {
                            return html`<div class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">👏</span>
                                <p>Alle Verträge im Griff</p>
                            </div>`;
                        }

                        return html` <ul part="employee-list">
                            ${employeeWithExpiringContract.map(
                                (entry) => html`
                                    <li
                                        part="employee"
                                        @click=${() => {
                                            router.go(
                                                `employees/${entry.employee.id}/contracts/view/${entry.contract?.id}`
                                            );
                                        }}
                                    >
                                        <ptc-avatar .employee=${entry.employee} part="employee-avatar"></ptc-avatar>
                                        <span class="semibold" part="employee-name">${entry.employee.name}</span>
                                        <span part="time-left">
                                            <span part="time-left-label">
                                                ${RelativeDayFormatter.format(
                                                    entry.contract?.end
                                                        ? Temporal.PlainDate.from(entry.contract.end)
                                                              .subtract({ days: 1 })
                                                              .since(Temporal.Now.plainDateISO()).days
                                                        : 0,
                                                    "days"
                                                )}
                                            </span>

                                            <ptc-popover trigger="hover" non-interactive preferAlignment="right">
                                                <span>
                                                    ${entry.contract?.end
                                                        ? FullDateFormatter.format(
                                                              Temporal.PlainDate.from(entry.contract.end).subtract({
                                                                  days: 1,
                                                              })
                                                          )
                                                        : ""}</span
                                                >
                                            </ptc-popover>
                                        </span>
                                    </li>
                                `
                            )}
                        </ul>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-expiring-contracts": DashboardTileExpiringContracts;
    }
}
